import { useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as Sentry from '@sentry/browser';
import { VideoContext } from '~/providers/VideoProvider';
import * as lib from '~/dependencies';
import { VideoState } from '~/store/reducers/callCurrent';
import { RootStateOrAny, useSelector } from 'react-redux';
import { electron, log } from '~/helpers';
import { checkVideoDevice } from '~/helpers/webrtc';

type ExtensionType = [
  {
    name: string;
    number: number;
    subscription: string;
  }
];

export const useStartVideoCall = () => {
  // CONTEXT'S
  const { socket, startCall } = useContext(VideoContext);

  // HOOK'S
  const { t } = lib.useTranslation();

  // SELECTOR'S
  const extensions = useSelector(
    (state: RootStateOrAny) => state.phone?.extensions
  ) as ExtensionType;
  const userName = useSelector(
    (state: RootStateOrAny) => state.auth?.user_name
  );
  const accountId = useSelector(
    (state: RootStateOrAny) => state.auth?.account_id
  );
  const userExtension = useSelector(
    (state: RootStateOrAny) => state.auth?.user_extension
  );

  // CONST'S
  const isValidAccount = accountId === 'IE-00491' || accountId === 'IE-00002';
  const hasVideoPermission = extensions.some(
    e =>
      (Number(e.number) === Number(userExtension) &&
        e.subscription?.toLocaleLowerCase() === 'enterprise') ||
      isValidAccount
  );
  const isVideoWebSocketEnabled = socket.current?.connected;

  const startVideoCall = async (dstExtNumber: number) => {
    try {
      const cameraPermission: string = await electron.requestCameraPermission();
      const hasCameraPermission = cameraPermission === 'granted';
      const hasCameraDevice = await checkVideoDevice();

      if (!window.isElectron) {
        throw new Error('Electron is not running...');
      }

      if (!hasCameraPermission) {
        throw new Error('no_camera_perm');
      }

      if (!hasCameraDevice) {
        throw new Error('no_camera_device');
      }

      if (socket.current && !socket.current.connected) {
        throw new Error('no_ws_conn');
      }

      if (dstExtNumber) {
        const extension = extensions.find(
          e => String(e.number) === String(dstExtNumber)
        );

        if (extension) {
          const from = `${accountId}-${userExtension}`;
          const to = `${accountId}-${extension.number}`;
          const data = {
            callId: uuidv4(),
            from,
            fromName: userName,
            to,
            toName: extension.name,
            state: VideoState.Initial,
            isInviter: true,
          };

          startCall({ ...data, roomUrl: null });

          return;
        }
      }

      throw new Error('An extension number MUST be provided.');
    } catch (err) {
      log.info(`NUACOM-LOG: [VIDEO] - ${err}`);
      electron.log(
        `NUACOM-LOG: [VIDEO] - Failure to start the video call. Reason: ${err}`
      );
      const { message } = err as any;

      if (message === 'no_camera_device') {
        lib.ToastMessage('error', t('screens.video.noVideoDevice'), 7000);
      } else if (message === 'no_camera_perm') {
        lib.ToastMessage('error', t('screens.video.noCameraPerm'), 7000);
      } else {
        lib.ToastMessage('error', t('screens.video.videoCallFailed'), 7000);
      }

      Sentry.captureMessage(
        `NUACOM-LOG: [VIDEO] - Failure to start the video call. Reason: ${message} - ${accountId}-${userExtension} `,
        Sentry.Severity.Info
      );
    }
  };

  return { hasVideoPermission, isVideoWebSocketEnabled, startVideoCall };
};
